import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Loader2, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request'); // 'request', 'sent', 'reset', 'success'
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenEmail, setTokenEmail] = useState('');

  // Verify token on mount if present
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/auth/verify-reset-token/${token}`);
      setTokenValid(true);
      setTokenEmail(res.data.email);
    } catch (error) {
      setTokenValid(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep('sent');
      toast.success('Email inviata!');
    } catch (error) {
      toast.error('Errore nell\'invio dell\'email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setStep('success');
      toast.success('Password reimpostata!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel reset della password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <span className="text-3xl font-light tracking-[0.3em] text-black">QANTRI</span>
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          
          {/* Step: Request Reset */}
          {step === 'request' && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#064E3B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="h-8 w-8 text-[#064E3B]" />
                </div>
                <h1 className="text-2xl font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Password dimenticata?
                </h1>
                <p className="text-neutral-500 text-sm">
                  Inserisci la tua email e ti invieremo un link per reimpostare la password.
                </p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                  <Input
                    type="email"
                    placeholder="La tua email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="reset-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#064E3B] hover:bg-[#043927] text-white"
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Invia link di reset'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-[#064E3B] hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Torna al login
                </Link>
              </div>
            </>
          )}

          {/* Step: Email Sent */}
          {step === 'sent' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Email inviata!
              </h1>
              <p className="text-neutral-500 text-sm mb-6">
                Se l'email <strong>{email}</strong> è registrata, riceverai un link per reimpostare la password.
                <br /><br />
                Controlla anche la cartella spam.
              </p>
              
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-12 bg-[#064E3B] hover:bg-[#043927] text-white"
              >
                Torna al login
              </Button>
            </div>
          )}

          {/* Step: Reset Form (with token) */}
          {step === 'reset' && tokenValid === true && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#064E3B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="h-8 w-8 text-[#064E3B]" />
                </div>
                <h1 className="text-2xl font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Nuova Password
                </h1>
                <p className="text-neutral-500 text-sm">
                  Inserisci la tua nuova password per <strong>{tokenEmail}</strong>
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Nuova password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 pr-10 h-12"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>

                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Conferma password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="confirm-password-input"
                  />
                </div>

                <p className="text-xs text-neutral-400">
                  La password deve essere di almeno 6 caratteri
                </p>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#064E3B] hover:bg-[#043927] text-white"
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Reimposta Password'
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Token Invalid */}
          {step === 'reset' && tokenValid === false && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Link non valido
              </h1>
              <p className="text-neutral-500 text-sm mb-6">
                Il link è scaduto o è già stato utilizzato.<br />
                Richiedi un nuovo link per reimpostare la password.
              </p>
              
              <Button
                onClick={() => { setStep('request'); navigate('/reset-password', { replace: true }); }}
                className="w-full h-12 bg-[#064E3B] hover:bg-[#043927] text-white"
              >
                Richiedi nuovo link
              </Button>
            </div>
          )}

          {/* Token Loading */}
          {step === 'reset' && tokenValid === null && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#064E3B] mx-auto" />
              <p className="text-neutral-500 mt-4">Verifica del link...</p>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Password reimpostata!
              </h1>
              <p className="text-neutral-500 text-sm mb-6">
                La tua password è stata aggiornata con successo.<br />
                Ora puoi accedere con la nuova password.
              </p>
              
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-12 bg-[#064E3B] hover:bg-[#043927] text-white"
              >
                Vai al Login
              </Button>
            </div>
          )}

        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
